import { useId, type InputHTMLAttributes } from "react";
import { IconAlert, IconCheck } from "../icons/icons";
import styles from "./Checkbox.module.css";

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  title: string;
  description?: string;
  required?: boolean;
  error?: string;
}

export function Checkbox({ title, description, required, error, id, className, ...rest }: CheckboxProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const descId = description ? `${inputId}-desc` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;

  return (
    <div className={[styles.row, className].filter(Boolean).join(" ")}>
      <input
        id={inputId}
        type="checkbox"
        className={styles.input}
        required={required}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={[descId, errorId].filter(Boolean).join(" ") || undefined}
        {...rest}
      />
      <label htmlFor={inputId} className={styles.control}>
        <span className={styles.box} aria-hidden="true">
          <IconCheck className={styles.icon} width={14} height={14} />
        </span>
        <span className={styles.text}>
          <span className={styles.title}>
            {title}
            {required && (
              <>
                <span className={styles.required} aria-hidden="true">
                  *
                </span>
                <span className={styles.srOnly}> Required</span>
              </>
            )}
          </span>
          {description && (
            <span id={descId} className={styles.description}>
              {description}
            </span>
          )}
          {error && (
            <span id={errorId} className={styles.error} role="alert">
              <IconAlert width={13} height={13} />
              {error}
            </span>
          )}
        </span>
      </label>
    </div>
  );
}
